"use client";

import React, { useState } from "react";
import { Building2, Users, ArrowRight, CircleCheck, Loader, Globe, Mail, Phone, Handshake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const partners = [{
  name: "Partner 1",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_alpha_1.png"
}, {
  name: "Partner 2",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_beta_2.png"
}, {
  name: "Partner 3",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_gamma_3.png"
}, {
  name: "Partner 4",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_delta_4.png"
}, {
  name: "Partner 5",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_epsilon_5.png"
}, {
  name: "Partner 6",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/partner_logo_zeta_6.png"
}];
const benefits = ["Access to 120,000+ Hispanic-owned businesses", "Strategic brand alignment with economic growth", "Exclusive data & research insights", "High-impact community visibility"];
export default function PartnershipInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="partnership-inquiry" className="dark bg-background text-foreground py-24 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="space-y-12">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                <Handshake className="size-4" />
                <span>Strategic Alliances</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Partner with <span className="text-primary">Purpose</span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Join a coalition of forward-thinking organizations driving economic equity and growth in Colorado. 
                The COHCC offers unique opportunities for corporate partners to engage with the state's 
                fastest-growing demographic and business sector.
              </p>

              <ul className="space-y-4 mt-6">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-5 text-secondary mt-0.5 shrink-0" />
                    <span className="text-foreground/90">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="space-y-6 pt-8 border-t border-border/50">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Trusted by Industry Leaders
              </h3>
              <div className="grid grid-cols-3 gap-6">
                {partners.map((partner, i) => <div key={i} data-index={i} className="group flex items-center justify-center p-4 rounded-lg bg-card/50 border border-border/50 hover:bg-card hover:border-primary/30 transition-all duration-300">
                    <img src={partner.logo} alt={partner.name} className="max-h-10 w-auto opacity-40 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                  </div>)}
              </div>
              <p className="text-sm text-muted-foreground">
                Join over 200+ corporate partners shaping the future of Colorado's economy.
              </p>
            </div>
          </div>

          {}
          <div className="lg:pl-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-2xl">
              <CardHeader className="pb-6 border-b border-border/50">
                <CardTitle className="text-2xl">Partnership Inquiry</CardTitle>
                <CardDescription>
                  Complete the form below to explore partnership opportunities. 
                  Our team will review your inquiry and respond within 48 hours.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formId="partnership_inquiry_form_join_us" sectionName="partnership-inquiry" defaultValues={{
                organization_name: "",
                contact_name: "",
                email: "",
                phone: "",
                partnership_type: "",
                website: "",
                message: ""
              }} validationRules={{
                organization_name: {
                  required: true
                },
                contact_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                partnership_type: {
                  required: true
                },
                website: {
                  pattern: "url"
                },
                message: {
                  required: true
                }
              }} submitText="Submit Partnership Request" successMessage="Thank you for your interest. A member of our partnership team will contact you shortly.">
                  <PartnershipForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}
function PartnershipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-green-500/10 flex items-center justify-center mb-2">
          <CircleCheck className="size-8 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {}
        <FormField control={control} name="organization_name" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Organization Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Colorado Hispanic Chamber of Commerce" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contact_name" rules={{
        required: "Contact person name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Users className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Maria Gonzalez" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormDescription>Full name of the primary contact.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {}
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                    <Input type="email" placeholder="maria@example.org" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                    <Input type="tel" placeholder="+1 (303) 555-1234" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <FormField control={control} name="partnership_type" rules={{
        required: "Please select a partnership type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Type of Partnership <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30" {...field}>
                    <option value="" disabled>Select a partnership type</option>
                    <option value="Sponsorship">Sponsorship</option>
                    <option value="Event Collaboration">Event Collaboration</option>
                    <option value="Research Partnership">Research Partnership</option>
                    <option value="Member Services">Member Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="website" rules={{
        pattern: {
          value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
          message: "Please enter a valid URL"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Organization Website</FormLabel>
              <FormControl>
                <div className="relative">
                  <Globe className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="url" placeholder="https://www.example.org" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="message" rules={{
        required: "Please provide details about your partnership inquiry"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message / Partnership Details <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your partnership idea, goals, and any relevant timelines..." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormDescription>Provide as much detail as possible to help us evaluate the opportunity.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
          <span className="text-destructive mt-0.5"></span>
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : <>
            Submit Partnership Request
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}